import { PARTNER_CITIES } from '../../content';
import { deriveNeighbourhoodFromAddress } from '../mocks/mockAvailability';
import type { BusinessListingPreview, PartnerBusiness, WeekHours, WizardPreviewDraft } from '../types';
import { isPartnerListingLive } from './subscription';

const CATEGORY_LABELS = {
  food: 'Café y restaurante',
  wellness: 'Pilates, yoga y gimnasio',
} as const;

const DAY_LABELS: Record<keyof WeekHours, string> = {
  mon: 'Lun',
  tue: 'Mar',
  wed: 'Mié',
  thu: 'Jue',
  fri: 'Vie',
  sat: 'Sáb',
  sun: 'Dom',
};

function formatHoursSummary(hours: WeekHours): string {
  const openDays = (Object.keys(hours) as (keyof WeekHours)[]).filter((day) => hours[day].open);
  if (openDays.length === 0) return 'Cerrado';

  const first = hours[openDays[0]];
  const sameHours = openDays.every(
    (day) => hours[day].from === first.from && hours[day].to === first.to,
  );

  if (sameHours && openDays.length === 7) {
    return `Todos los días · ${first.from}–${first.to}`;
  }

  if (sameHours && openDays.length >= 5) {
    const range = `${DAY_LABELS[openDays[0]]}–${DAY_LABELS[openDays[openDays.length - 1]]}`;
    return `${range} · ${first.from}–${first.to}`;
  }

  return openDays
    .map((day) => `${DAY_LABELS[day]} ${hours[day].from}–${hours[day].to}`)
    .join(' · ');
}

function resolveField<T>(live: T | undefined, saved: T | undefined, fallback: T): T {
  if (live !== undefined) return live;
  if (saved !== undefined) return saved;
  return fallback;
}

function isOpenNow(hours: WeekHours): boolean {
  const dayKeys: (keyof WeekHours)[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const day = hours[dayKeys[new Date().getDay()]];
  if (!day?.open) return false;

  const toMinutes = (value: string) => {
    const [h, m] = value.split(':').map(Number);
    return h * 60 + m;
  };

  const now = new Date();
  const current = now.getHours() * 60 + now.getMinutes();
  return current >= toMinutes(day.from) && current <= toMinutes(day.to);
}

export function buildListingPreview(
  business: PartnerBusiness | null,
  liveDraft: WizardPreviewDraft = {},
): BusinessListingPreview {
  const cityId = resolveField(liveDraft.cityId, business?.cityId, 'barcelona');
  const category = resolveField(liveDraft.category, business?.category, 'food');
  const address = liveDraft.address ?? business?.address ?? null;
  const name = liveDraft.name ?? business?.name ?? null;
  const description = liveDraft.description ?? business?.description ?? null;
  const website = liveDraft.website ?? business?.website ?? null;
  const instagram = liveDraft.instagram ?? business?.instagram ?? null;
  const hours = liveDraft.hours ?? business?.hours ?? null;
  const hoursEnabled = resolveField(
    liveDraft.hoursEnabled,
    business?.hoursEnabled,
    category === 'food',
  );
  const classes = liveDraft.classes ?? business?.classes ?? [];
  const photoCount = liveDraft.photoCount ?? business?.photoCount ?? 0;
  const photoPreviewUrls =
    liveDraft.photoPreviewUrls ??
    business?.photoPreviewUrls ??
    business?.importedPhotoUrls ??
    [];

  const neighbourhood =
    address && address.trim().length > 0
      ? deriveNeighbourhoodFromAddress(address, cityId).label
      : business?.neighbourhood ?? null;

  const cityLabel = PARTNER_CITIES.find((city) => city.id === cityId)?.label ?? cityId;
  const categoryLabel = CATEGORY_LABELS[category];

  const missingFields: string[] = [];
  if (!name?.trim()) missingFields.push('name');
  if (!address?.trim()) missingFields.push('address');
  if (!description?.trim()) missingFields.push('description');
  if (photoCount < 1 && photoPreviewUrls.length < 1) missingFields.push('photos');

  const filledCount = 4 - missingFields.length;
  const completeness = Math.round((filledCount / 4) * 100);

  const showHours = category === 'food' || hoursEnabled;

  return {
    name: name?.trim() || null,
    cityId,
    cityLabel,
    category,
    categoryLabel,
    address: address?.trim() || null,
    neighbourhood,
    description: description?.trim() || null,
    website: website?.trim() || null,
    instagram: instagram?.trim() || null,
    hoursSummary: showHours && hours ? formatHoursSummary(hours) : null,
    hours: showHours ? hours : null,
    hoursEnabled: showHours,
    isOpenNow: showHours && hours ? isOpenNow(hours) : false,
    classes: category === 'wellness' ? classes : [],
    photoCount: Math.max(photoCount, photoPreviewUrls.length),
    photoPreviewUrls,
    hasPhotos: photoCount > 0 || photoPreviewUrls.length > 0,
    isPartner: business ? isPartnerListingLive(business) : false,
    completeness,
    missingFields,
  };
}
